/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   Salary Structures — list screen.

   Type-aware: a coloured Structure Type badge and a per-type "Key Details"
   column instead of one column per salary component (most of which would be
   blank for a given type). Add / Edit / View open dedicated SCREENS via the
   router. Structure-type filter tabs sit beside the search. Loading / empty
   states come from the shared DataTable.
   ──────────────────────────────────────────────────────────────────────── */
import DataTable from "components/DataTable";
import HeaderButtons from "components/Button/HeaderButtons";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaEye } from "react-icons/fa";
import apiService from "services/apiService";
import { inferStructureType } from "./salaryStructure.mapper";
import { StructureType, TemplateId, TYPE_BADGE, templateForType } from "./salaryStructure.config";
import { formatINR, monthsLabel } from "./salaryStructure.calc";
import { BannerContext } from "components/SideBar";

const TypeBadge = ({ type }: { type: StructureType }) => {
  const badge = TYPE_BADGE[type];
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge.className}`}>{badge.label}</span>;
};

// Small cell formatters shared by the template columns.
const pct = (v: any) => (v != null ? `${v}%` : "—");
const money = (v: any, suffix: string) => (v != null ? `${formatINR(Number(v))}${suffix}` : "—");
const yesNo = (on: any) => (on ? "Yes" : "No");

// Middle columns shown per active tab. Each tab surfaces only the fields that
// its own field group populates, so no column is blank for the visible rows.
const TEMPLATE_COLUMNS: Record<TemplateId, any[]> = {
  EMPLOYEE: [
    { key: "basicSalaryPercentage", label: "Basic", render: (_v: any, r: any) => pct(r.basicSalaryPercentage) },
    { key: "hraPercentage", label: "HRA", render: (_v: any, r: any) => pct(r.hraPercentage) },
    { key: "pf", label: "PF", excludeExport: true, render: (_v: any, r: any) => yesNo(r.pfApplicable) },
    { key: "esi", label: "ESI", excludeExport: true, render: (_v: any, r: any) => yesNo(r.esiApplicable) },
  ],
  CONTRACT: [
    {
      key: "gst",
      label: "GST",
      excludeExport: true,
      render: (_v: any, r: any) => (r.gstRequired === true ? "With GST" : "Without GST"),
    },
    {
      key: "contractValue",
      label: "Contract Value",
      excludeExport: true,
      render: (_v: any, r: any) => money(r.gstRequired === true ? r.gstContractValue : r.nonGstContractValue, " / yr"),
    },
    {
      key: "monthlyPayout",
      label: "Monthly Payout",
      excludeExport: true,
      render: (_v: any, r: any) => money(r.gstRequired === true ? r.gstMonthlyPayout : r.nonGstMonthlyPayout, " / mo"),
    },
    {
      key: "tds",
      label: "TDS",
      excludeExport: true,
      render: (_v: any, r: any) => pct(r.gstRequired === true ? r.gstTdsRate : r.nonGstTdsRate),
    },
  ],
  TRAINEE: [
    {
      key: "stipend",
      label: "Monthly Stipend",
      excludeExport: true,
      render: (_v: any, r: any) => money(r.partTimeMonthlyPayout, " / mo"),
    },
    {
      key: "period",
      label: "Period",
      excludeExport: true,
      render: (_v: any, r: any) => (r.partTimePeriod ? monthsLabel(parseInt(String(r.partTimePeriod), 10)) : "—"),
    },
    // { key: "pf", label: "PF", excludeExport: true, render: (_v: any, r: any) => yesNo(r.pfApplicable) },
    // { key: "esi", label: "ESI", excludeExport: true, render: (_v: any, r: any) => yesNo(r.esiApplicable) },
  ],
};

// Filter tabs grouped by TEMPLATE so Contractor/Consultant and Trainee/Intern
// each collapse into a single tab (matching the combined Add-form cards). No
// "All" tab — a tab is always active so the table can show type-specific columns.
const TYPE_TABS: Array<{ key: TemplateId; label: string }> = [
  { key: "EMPLOYEE", label: "Permanent" },
  { key: "CONTRACT", label: "Contractor & Consultant" },
  { key: "TRAINEE", label: "Trainee & Intern" },
];

const SalaryStructureDetails = () => {
  const navigate = useNavigate();
  const [structurData, setStructureData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TemplateId>("EMPLOYEE");

     const { isBannerVisible } = useContext(BannerContext);
          const dynamicHeightClass = isBannerVisible
            ? "h-[calc(100vh-112px)] min-h-[300px]"
            : "h-[calc(100vh-80px)] min-h-[300px]";

  useEffect(() => {
    setLoading(true);
    apiService
      .get(`api/salary/getAllSalaryStructures`)
      .then((response) => {
        setStructureData(Array.isArray(response.data) ? response.data : [response.data]);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Annotate each row with its structure type once.
  const rows = useMemo(
    () => structurData.map((r) => ({ ...r, __type: inferStructureType(r) as StructureType })),
    [structurData]
  );

  // Count by template so a tab tallies both of its combined types.
  const counts = useMemo(() => {
    const c: Record<string, number> = { EMPLOYEE: 0, CONTRACT: 0, TRAINEE: 0 };
    rows.forEach((r) => {
      const template = templateForType(r.__type);
      c[template] = (c[template] ?? 0) + 1;
    });
    return c;
  }, [rows]);

  const filteredRows = useMemo(
    () => rows.filter((r) => templateForType(r.__type) === activeTab),
    [rows, activeTab]
  );

  // Columns rebuild per active tab: shared Name + Type, then the tab's own
  // field columns, then Actions.
  const tableColumn = useMemo(
    () => [
      {
        key: "structureName",
        label: "Structure Name",
        render: (_v: any, row: any) => (
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">{row.structureName}</div>
            {row.description && <div className="text-xs text-slate-400 truncate max-w-[240px]">{row.description}</div>}
          </div>
        ),
      },
      { key: "__type", label: "Type", render: (_v: any, row: any) => <TypeBadge type={row.__type} /> },
      ...TEMPLATE_COLUMNS[activeTab].map((col) => ({
        ...col,
        render: (_v: any, row: any) => (
          <span className="text-sm text-slate-600 dark:text-slate-300">{col.render(_v, row)}</span>
        ),
      })),
      {
        key: "actions",
        label: "Actions",
        excludeExport: true,
        actions: (row: any) => (
          <div className="flex items-center gap-2">
            <span
              onClick={() => navigate("/admin/salaryStructure/view", { state: { record: row } })}
              className="px-2 py-2 rounded-lg cursor-pointer text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
              title="View"
            >
              <FaEye />
            </span>
            <span
              onClick={() => navigate("/admin/salaryStructure/edit", { state: { record: row } })}
              className="btn-primary px-2 py-2 rounded-lg cursor-pointer"
              title="Edit"
            >
              <FaEdit />
            </span>
          </div>
        ),
      },
    ],
    [activeTab, navigate]
  );

  const filterTabs = (
    <div className="flex items-center gap-2 flex-wrap">
      {TYPE_TABS.map((tab) => (
        <HeaderButtons
          key={tab.key}
          size="small"
          variant={activeTab === tab.key ? "selected" : "inactive"}
          onClick={() => setActiveTab(tab.key)}
        >
          {tab.label} ({counts[tab.key] ?? 0})
        </HeaderButtons>
      ))}
    </div>
  );

  return (
    <div className="text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300">
      {/* Page header */}


      <div className="bg-white dark:bg-gray-800">
        <DataTable
          data={filteredRows}
          columns={tableColumn}
          loading={loading}
          onAddBtnClick={() => navigate("/admin/salaryStructure/add")}
          leftSideButtons={filterTabs}
          isExportable={false}
          heightClass={dynamicHeightClass}
        />
      </div>
    </div>
  );
};

export default SalaryStructureDetails;
